import React from "react";
import { Youtube, ArrowLeft } from "lucide-react";
import { SiteFooter } from "./SiteFooter";

const TODAY = new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" updated={TODAY} testid="privacy-page">
      <H2>1. Who we are</H2>
      <P>
        VidROI (the "Service") is operated by <strong>Remarkable Creators Ltd</strong>,
        a company registered in England &amp; Wales. We act as the data controller
        for the personal data we collect through <a href="https://vidroi.io">vidroi.io</a>.
        Contact us at <a href="mailto:privacy@vidroi.io">privacy@vidroi.io</a>.
      </P>

      <H2>2. Information we collect</H2>
      <P>We collect the following categories of data:</P>
      <UL>
        <li><B>Account data</B> — your email address, name, and profile picture received from Google when you sign in.</li>
        <li><B>YouTube data (via Google OAuth)</B> — your channel ID, channel name, video list, view counts, like and comment counts. Used to display sales attribution on your dashboard. When you explicitly click "Inject link" or "Inject all" in the dashboard, VidROI will modify the description of the selected video(s) to insert your tracking short link in a clearly-marked block — we never modify your videos automatically or without your action, and you can remove the block at any time using the same UI. Scope used: <code>youtube.force-ssl</code>.</li>
        <li><B>Billing data</B> — handled by Stripe, our payment processor. We store only Stripe customer and subscription identifiers, never card numbers.</li>
        <li><B>Tracking events</B> — clicks and sales captured by the JavaScript pixel you install on your own website. Each event includes the UTM parameters, video ID, timestamp, and (for sales) the sale amount and transaction ID.</li>
        <li><B>Cookies</B> — a single httpOnly session cookie used for authentication. We do not use marketing or third-party tracking cookies on vidroi.io.</li>
      </UL>

      <H2>3. How we use your data</H2>
      <UL>
        <li>To display per-video revenue analytics on your dashboard.</li>
        <li>To process subscription payments through Stripe.</li>
        <li>To detect and prevent abuse, fraud, and security incidents.</li>
        <li>To respond to support requests.</li>
        <li>To send service-critical emails (e.g. billing receipts, password-reset confirmations). We do not send marketing emails without explicit opt-in.</li>
      </UL>

      <H2>4. YouTube API Services and Google data</H2>
      <P>
        VidROI uses YouTube API Services. By connecting your YouTube account you
        also agree to{" "}
        <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
          YouTube's Terms of Service
        </a>{" "}
        and{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          Google's Privacy Policy
        </a>.
        We comply with the Google API Services User Data Policy, including the
        Limited Use requirements.
      </P>
      <P>
        Specifically: we use Google user data only to provide and improve
        user-facing features of VidROI; we never sell it, transfer it for ads,
        or use it for credit-worthiness decisions; only humans access raw data
        when you give us permission (e.g. for support), for security purposes,
        or to comply with applicable law.
      </P>
      <P>
        You can revoke our access at any time:
        (a) inside VidROI via Onboarding → Disconnect, or
        (b) at{" "}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
          myaccount.google.com/permissions
        </a>.
      </P>

      <H2>5. Sharing and processors</H2>
      <P>We share data only with the processors below, under data-processing agreements:</P>
      <UL>
        <li><B>Google LLC</B> — sign-in &amp; YouTube data.</li>
        <li><B>Stripe Payments Europe Ltd</B> — payment processing.</li>
        <li><B>MongoDB Inc.</B> — managed database hosting.</li>
        <li><B>Emergent Labs</B> — application hosting.</li>
        <li><B>Anthropic PBC</B> — AI hook analysis (only the competitor video transcripts you submit; we do not send your own channel content).</li>
      </UL>

      <H2>6. Where we store data</H2>
      <P>
        Data is stored on servers operated by Emergent Labs and MongoDB Atlas,
        within the EU/EEA where possible. Stripe and Google may process data in
        the United States under Standard Contractual Clauses or equivalent
        safeguards.
      </P>

      <H2>7. Retention</H2>
      <UL>
        <li>YouTube channel and video data is refreshed on each session and retained while your subscription is active.</li>
        <li>Tracking events are retained for the lifetime of your account so historical reports remain available.</li>
        <li>Account data is retained until you request deletion. We delete all data within 30 days of an erasure request, except where retention is legally required (e.g. invoices for 6 years under UK accounting law).</li>
      </UL>

      <H2>8. Your rights (UK GDPR / EU GDPR)</H2>
      <P>You can:</P>
      <UL>
        <li>Request a copy of the personal data we hold about you.</li>
        <li>Request correction of inaccurate data.</li>
        <li>Request deletion of your account and data.</li>
        <li>Object to or restrict processing.</li>
        <li>Withdraw consent at any time (e.g. by disconnecting YouTube).</li>
        <li>Lodge a complaint with the UK Information Commissioner's Office at <a href="https://ico.org.uk/">ico.org.uk</a>.</li>
      </UL>
      <P>To exercise any of these rights, email <a href="mailto:privacy@vidroi.io">privacy@vidroi.io</a>.</P>

      <H2>9. Security</H2>
      <P>
        We use TLS in transit, encryption at rest, role-based access controls,
        and signed httpOnly cookies. OAuth refresh tokens are stored encrypted.
        No internet-facing service is 100% secure — if you suspect a breach,
        email <a href="mailto:security@vidroi.io">security@vidroi.io</a> immediately.
      </P>

      <H2>10. Children</H2>
      <P>VidROI is not directed at people under 16. We do not knowingly collect data from minors.</P>

      <H2>11. Changes</H2>
      <P>
        We may update this policy. We'll post material changes on this page and,
        for significant changes, notify you by email at least 14 days before
        they take effect.
      </P>

      <H2>12. Contact</H2>
      <P>
        Remarkable Creators Ltd<br />
        Email: <a href="mailto:privacy@vidroi.io">privacy@vidroi.io</a>
      </P>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" updated={TODAY} testid="terms-page">
      <H2>1. Acceptance</H2>
      <P>
        By creating an account at <a href="https://vidroi.io">vidroi.io</a> you
        agree to these Terms. The Service is provided by{" "}
        <strong>Remarkable Creators Ltd</strong> ("we", "us"). If you do not
        agree, do not use the Service.
      </P>

      <H2>2. The Service</H2>
      <P>
        VidROI is a YouTube sales-attribution dashboard. We help creators see
        which videos drive revenue by combining YouTube channel statistics
        (via the YouTube Data API v3) with a JavaScript pixel installed on
        your own website.
      </P>

      <H2>3. Account &amp; security</H2>
      <UL>
        <li>You authenticate via Google Sign-In. You're responsible for keeping the connected Google account secure.</li>
        <li>One account per person or legal entity. Sharing credentials is prohibited.</li>
        <li>You're responsible for everything done under your account.</li>
      </UL>

      <H2>4. Plans, billing &amp; refunds</H2>
      <UL>
        <li>Paid plans are billed monthly in advance via Stripe.</li>
        <li>Starter ($29/mo) — fair-use caps on the AI features (250 competitor analyses, 60 hook blends, 200 Ask AI messages / month). Pro ($79/mo) — unlimited AI analysis + Claude Sonnet-powered Ask AI. Both plans include unlimited tracked offers and connected channels.</li>
        <li><B>30-day money-back guarantee</B> — if you're not satisfied within the first 30 days of your first paid month, reply to your receipt and we'll refund every cent. Renewals are non-refundable except where required by law.</li>
        <li>You can cancel at any time from the Stripe Billing Portal. Cancellation takes effect at the end of the current billing period.</li>
        <li>We may change pricing for new sign-ups at any time; existing subscribers get 30 days' notice before any price change.</li>
      </UL>

      <H2>5. Acceptable use</H2>
      <P>You agree not to:</P>
      <UL>
        <li>Use the Service for any unlawful purpose, or to track sales for content you don't own/operate.</li>
        <li>Attempt to access another user's data or circumvent our authentication or rate limits.</li>
        <li>Resell, sublicense, or build a competing analytics product directly on top of the Service.</li>
        <li>Use the JavaScript pixel on pages that violate applicable consumer protection or privacy laws (e.g. failing to provide a cookie notice where one is required).</li>
        <li>Use the Service to violate{" "}
          <a href="https://developers.google.com/youtube/terms/api-services-terms-of-service" target="_blank" rel="noopener noreferrer">
            YouTube API Services Terms of Service
          </a>{" "}
          or the YouTube API Developer Policies.
        </li>
      </UL>

      <H2>6. Your content &amp; data</H2>
      <P>
        You retain all rights to your YouTube content, your website data, and
        the sales events captured by the pixel. We get a non-exclusive licence
        to process this data solely to provide the Service.
      </P>

      <H2>7. Third-party services</H2>
      <P>
        The Service depends on Google APIs (YouTube Data API v3, Google
        Sign-In) and Stripe. Their availability and terms apply alongside
        these Terms. We're not responsible for outages or changes in their
        APIs.
      </P>

      <H2>8. Termination</H2>
      <UL>
        <li>You can delete your account at any time — email <a href="mailto:support@vidroi.io">support@vidroi.io</a>.</li>
        <li>We may suspend or terminate your account if you breach these Terms or use the Service in a way that damages it or other users. We'll give reasonable notice unless the breach is serious.</li>
      </UL>

      <H2>9. Disclaimer</H2>
      <P>
        The Service is provided "as is". We don't warrant that it'll be
        uninterrupted, error-free, or that the analytics will be 100% accurate
        — attribution depends on UTMs being preserved through the user's
        journey, ad-blockers, etc.
      </P>

      <H2>10. Limitation of liability</H2>
      <P>
        To the maximum extent permitted by law, our total liability to you in
        any 12-month period is limited to the amount you paid us in that
        period. We're not liable for indirect, incidental, or consequential
        damages, including lost revenue or lost profits.
      </P>

      <H2>11. Governing law</H2>
      <P>
        These Terms are governed by the laws of England &amp; Wales. Disputes
        will be resolved by the courts of England &amp; Wales, except where
        consumer-protection rules grant you the right to bring proceedings in
        your country of residence.
      </P>

      <H2>12. Changes</H2>
      <P>
        We may update these Terms. Material changes will be announced by
        email at least 14 days before they take effect.
      </P>

      <H2>13. Contact</H2>
      <P>
        Remarkable Creators Ltd<br />
        Email: <a href="mailto:support@vidroi.io">support@vidroi.io</a>
      </P>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Shared shell + typography
// ---------------------------------------------------------------------------
function LegalShell({ title, updated, children, testid }) {
  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 noise" data-testid={testid}>
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shadow-[0_0_18px_rgba(255,0,0,0.25)]">
              <Youtube className="w-4 h-4 text-red-500" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-semibold tracking-tight text-sm">
                Vid<span className="text-red-500 drop-shadow-[0_0_10px_rgba(255,0,0,0.5)]">ROI</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Analytics</div>
            </div>
          </a>
          <a href="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to app
          </a>
        </div>
      </header>
      <main className="relative max-w-3xl mx-auto px-6 py-12 sm:py-16 animate-fade-up">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">
          Legal · Last updated {updated}
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05] mb-3">{title}</h1>
        <div className="text-sm text-zinc-500 mb-10">Remarkable Creators Ltd — operator of vidroi.io</div>
        <article className="space-y-2 text-[15px] leading-relaxed text-zinc-300">
          {children}
        </article>
      </main>
      <SiteFooter variant="minimal" />
    </div>
  );
}

function H2({ children }) {
  return <h2 className="text-lg font-semibold text-white tracking-tight mt-8 mb-2">{children}</h2>;
}
function P({ children }) {
  return <p className="text-zinc-300 leading-relaxed">{children}</p>;
}
function UL({ children }) {
  return <ul className="space-y-1.5 list-disc list-outside ml-5 text-zinc-300">{children}</ul>;
}
function B({ children }) {
  return <strong className="text-white">{children}</strong>;
}
